<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import SubjectSection from '@/components/common/SubjectSection.vue'
import InlineField from '@/components/common/InlineField.vue'
import CoordinateInput from '@/components/ui/CoordinateInput.vue'
import CompletenessIndicator from './CompletenessIndicator.vue'
import PhotoUpload from './PhotoUpload.vue'
import FlyerGenerator from '@/components/flyer/FlyerGenerator.vue'
import FosButton from '@/components/ui/FosButton.vue'
import { useSubjectsStore } from '@/stores/subjects'
import type { Subject } from '@/types'
import { SUBJECT_CATEGORIES, MOBILITY_OPTIONS, TERRAIN_OPTIONS } from '@/types'

const props = defineProps<{
  subject: Subject
  incidentId: string
}>()

const showFlyerGenerator = ref(false)

const displayName = computed(() => {
  const parts = [props.subject.first_name, props.subject.last_name].filter(Boolean)
  return parts.length ? parts.join(' ') : 'Unknown Subject'
})

const subjectsStore = useSubjectsStore()
const coordSaved = ref(false)
let coordSavedTimer: ReturnType<typeof setTimeout> | null = null

async function onCoordUpdate(coords: { lat: number | null; lng: number | null }) {
  await Promise.all([
    subjectsStore.updateSubjectField(props.incidentId, props.subject.subject_id, 'lkp_lat', coords.lat),
    subjectsStore.updateSubjectField(props.incidentId, props.subject.subject_id, 'lkp_lng', coords.lng),
  ])
  coordSaved.value = true
  if (coordSavedTimer) clearTimeout(coordSavedTimer)
  coordSavedTimer = setTimeout(() => { coordSaved.value = false }, 2000)
}

onUnmounted(() => { if (coordSavedTimer) clearTimeout(coordSavedTimer) })

async function toggleTerrain(terrain: string) {
  const current: string[] = Array.isArray(props.subject.terrain_types) ? [...props.subject.terrain_types] : []
  const idx = current.indexOf(terrain)
  if (idx === -1) {
    current.push(terrain)
  } else {
    current.splice(idx, 1)
  }
  await subjectsStore.updateSubjectField(props.incidentId, props.subject.subject_id, 'terrain_types', current)
}
</script>

<template>
  <FlyerGenerator
    v-model:visible="showFlyerGenerator"
    :subject="subject"
    :incident-id="incidentId"
  />

  <div class="p-4 space-y-2">
    <!-- Subject nameplate -->
    <div class="border border-border rounded bg-surface p-4">
      <div class="flex items-start gap-4">
        <PhotoUpload :subject-id="subject.subject_id" :photo-url="subject.photo_url" size="lg" />
        <div class="flex-1 min-w-0">
          <div class="flex items-start justify-between gap-2">
            <h2 class="text-lg font-headline font-black text-on-surface uppercase tracking-tight leading-tight truncate">
              {{ displayName }}
            </h2>
            <FosButton variant="secondary" size="sm" class="shrink-0" @click="showFlyerGenerator = true">
              <span class="material-symbols-outlined text-[14px]">picture_as_pdf</span>
              Flyer
            </FosButton>
          </div>
          <div class="flex items-center gap-2 mt-1 flex-wrap">
            <span v-if="subject.age" class="text-xs font-mono text-on-surface-variant">{{ subject.age }}y</span>
            <span v-if="subject.age && subject.gender" class="text-outline text-xs">·</span>
            <span v-if="subject.gender" class="text-xs font-mono text-on-surface-variant">{{ subject.gender }}</span>
            <span v-if="subject.age && subject.gender" class="text-outline text-xs">·</span>
            <span
              v-if="subject.risk_category"
              class="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 bg-primary-container text-on-primary-container rounded ml-1"
            >{{ subject.risk_category }}</span>
          </div>
          <div class="mt-3">
            <CompletenessIndicator :subject="subject" />
          </div>
        </div>
      </div>
    </div>

    <!-- Identity -->
    <SubjectSection label="Identity" :always-open="true">
      <div class="grid grid-cols-2 gap-x-6 gap-y-3">
        <InlineField :subject-id="subject.subject_id" field="first_name" label="First Name" type="text" />
        <InlineField :subject-id="subject.subject_id" field="last_name" label="Last Name" type="text" />
        <InlineField :subject-id="subject.subject_id" field="age" label="Age" type="text" />
        <InlineField
          :subject-id="subject.subject_id"
          field="gender"
          label="Gender"
          type="select"
          :options="['Male', 'Female', 'Non-binary', 'Unknown']"
        />
        <InlineField
          :subject-id="subject.subject_id"
          field="subject_category"
          label="Category"
          type="select"
          :options="[...SUBJECT_CATEGORIES]"
        />
        <InlineField :subject-id="subject.subject_id" field="risk_category" label="Risk Category" type="text" />
      </div>
    </SubjectSection>

    <!-- Physical Description -->
    <SubjectSection label="Physical Description" :always-open="true">
      <div class="grid grid-cols-2 gap-x-6 gap-y-3">
        <InlineField :subject-id="subject.subject_id" field="height" label="Height" type="text" />
        <InlineField :subject-id="subject.subject_id" field="weight" label="Weight" type="text" />
        <InlineField :subject-id="subject.subject_id" field="race" label="Race" type="text" />
        <InlineField :subject-id="subject.subject_id" field="complexion" label="Complexion" type="text" />
        <InlineField :subject-id="subject.subject_id" field="hair_color" label="Hair Color" type="text" />
        <InlineField :subject-id="subject.subject_id" field="eye_color" label="Eye Color" type="text" />
      </div>
      <InlineField :subject-id="subject.subject_id" field="clothing_description" label="Clothing Description" type="textarea" />
    </SubjectSection>

    <!-- Last Known Point -->
    <SubjectSection label="Last Known Point" :always-open="true">
      <InlineField :subject-id="subject.subject_id" field="lkp_description" label="LKP Description" type="textarea" />
      <div class="flex flex-col gap-0.5">
        <CoordinateInput
          :lat="subject.lkp_lat"
          :lng="subject.lkp_lng"
          label="LKP Coordinates"
          @update:coords="onCoordUpdate"
        />
        <span v-if="coordSaved" class="text-[10px] text-primary font-mono uppercase tracking-widest">Saved</span>
      </div>
      <div class="grid grid-cols-2 gap-x-6 gap-y-3">
        <InlineField :subject-id="subject.subject_id" field="lkp_time" label="LKP Time" type="time" />
        <InlineField :subject-id="subject.subject_id" field="lkp_witness" label="Witness" type="text" />
      </div>
    </SubjectSection>

    <!-- Medical & Behaviour -->
    <SubjectSection label="Medical & Behaviour" :default-open="true">
      <InlineField :subject-id="subject.subject_id" field="medical_conditions" label="Medical Conditions" type="textarea" />
      <InlineField :subject-id="subject.subject_id" field="medications" label="Medications" type="textarea" />
      <div class="grid grid-cols-2 gap-x-6 gap-y-3">
        <InlineField
          :subject-id="subject.subject_id"
          field="mobility"
          label="Mobility"
          type="select"
          :options="[...MOBILITY_OPTIONS]"
        />
        <InlineField
          :subject-id="subject.subject_id"
          field="experience_level"
          label="Experience Level"
          type="select"
          :options="['None', 'Low', 'Moderate', 'High']"
        />
        <InlineField :subject-id="subject.subject_id" field="survival_training" label="Survival Training" type="text" />
      </div>
      <InlineField :subject-id="subject.subject_id" field="behavior_if_lost" label="Behavior if Lost" type="textarea" />
    </SubjectSection>

    <!-- Search Context -->
    <SubjectSection label="Search Context" :default-open="true">
      <div class="flex flex-col gap-1.5">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">Terrain Type</label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="t in TERRAIN_OPTIONS"
            :key="t"
            type="button"
            class="px-3 py-1.5 font-mono text-xs uppercase tracking-widest font-bold rounded transition-colors focus:outline-none"
            :class="
              subject.terrain_types?.includes(t)
                ? 'bg-primary-container text-white'
                : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest'
            "
            @click="toggleTerrain(t)"
          >{{ t }}</button>
        </div>
      </div>
    </SubjectSection>

    <!-- Plans & Route -->
    <SubjectSection label="Plans & Route" :default-open="false">
      <div class="grid grid-cols-2 gap-x-6 gap-y-3">
        <InlineField :subject-id="subject.subject_id" field="intended_dest" label="Intended Destination" type="text" />
        <InlineField :subject-id="subject.subject_id" field="expected_return" label="Expected Return" type="text" />
        <InlineField :subject-id="subject.subject_id" field="overdue_by" label="Overdue By" type="text" />
      </div>
      <InlineField :subject-id="subject.subject_id" field="planned_route" label="Planned Route" type="textarea" />
    </SubjectSection>

    <!-- Vehicle -->
    <SubjectSection label="Vehicle" :default-open="false">
      <div class="grid grid-cols-1 gap-y-3">
        <InlineField :subject-id="subject.subject_id" field="vehicle_description" label="Vehicle Description" type="text" />
        <InlineField :subject-id="subject.subject_id" field="vehicle_location" label="Vehicle Location" type="text" />
      </div>
    </SubjectSection>

    <!-- Companions -->
    <SubjectSection label="Companions" :default-open="false">
      <InlineField
        :subject-id="subject.subject_id"
        field="companions"
        label="Companions"
        type="select"
        :options="['Alone', 'With others', 'Unknown']"
      />
      <InlineField :subject-id="subject.subject_id" field="companion_names" label="Companion Names" type="textarea" />
    </SubjectSection>

    <!-- Reporting Party -->
    <SubjectSection label="Reporting Party" :default-open="false">
      <div class="grid grid-cols-2 gap-x-6 gap-y-3">
        <InlineField :subject-id="subject.subject_id" field="reporter_name" label="Reporter Name" type="text" />
        <InlineField :subject-id="subject.subject_id" field="reporter_phone" label="Reporter Phone" type="tel" />
        <InlineField :subject-id="subject.subject_id" field="reporter_relationship" label="Relationship" type="text" />
        <InlineField :subject-id="subject.subject_id" field="how_reported" label="How Reported" type="text" />
      </div>
    </SubjectSection>
  </div>
</template>
